import { EmbeddedRecordsMixin } from '@ember-data/serializer/rest';
import ApplicationSerializer from '@square/dashboard-data/serializers/application';
import { TeamMember } from '@square/dashboard-protos/models/squareup/team_api/resources/datatypes';
import { inject as service } from '@ember/service';
import { parsePhoneNumber } from 'libphonenumber-js';
const {
  ACTIVE,
  INACTIVE
} = TeamMember.Status;
const {
  ALL_CURRENT_AND_FUTURE_LOCATIONS,
  EXPLICIT_LOCATIONS
} = TeamMember.AssignedLocations.AssignmentType;
function formatPhoneNumber(phoneNumber, countryCode) {
  try {
    const phone = parsePhoneNumber(phoneNumber, countryCode);
    return phone.number; // E.164 format
  } catch {
    return phoneNumber; // attempt to save as is
  }
}
export default ApplicationSerializer.extend(EmbeddedRecordsMixin, {
  primaryKey: 'token',
  userInfo: service(),
  keyForRelationship(key) {
    switch (key) {
      case 'units':
        return 'subunit_tokens';
      case 'employeeRole':
        return 'employee_role_token';
      default:
        throw new Error(`Unknown key ${key}`);
    }
  },
  // The server sends employee role tokens in an array, but we really only
  // support one employee role per employee.
  normalize(typeClass, json) {
    if (json.token) {
      // Data fetched from the server has an `id` field, not a `token` field.
      // Therefore, if `json` has a `token` field, it was loaded from the
      // store and we don't need to convert it.
      return this._super(typeClass, json);
    }
    const can_access_all_units = json.assignedLocations?.assignmentType === ALL_CURRENT_AND_FUTURE_LOCATIONS;
    const deserializedJson = {
      active: json.status === ACTIVE,
      badge_pairing: json.badgePairing,
      can_access_all_units,
      can_be_managed: json.canBeManaged,
      employee_number: json.referenceId || null,
      first_name: json.givenName,
      has_passcode: json.hasPasscode,
      initials: json.initials,
      invitation_status: json.invitationStatus,
      invite_eligibility_status: json.inviteEligibilityStatus,
      is_account_owner: json.isOwner,
      is_authorized_user: json.isAuthorizedRepresentative,
      job_template_ids: json.jobTemplateIds ?? [],
      last_name: json.familyName,
      merchant_supplied_email: json.emailAddress ?? null,
      phone_number: json.phoneNumber ?? null,
      subunit_tokens: json.assignedLocations?.locationIds ?? [],
      token: json.id,
      associated_products: json.associatedProducts ?? {},
      person_id: json.personId ?? null,
      birth_date: json.birthDate ?? null,
      address: json.address ?? null,
      compliance_required_fields: json.complianceRequiredFields ?? []
    };
    if (json.permissionSetId) {
      deserializedJson.employee_role_token = json.permissionSetId;

      // Duplicating with a new property name (that will be exposed in
      // the Employee model) instead of renaming the existing property,
      // which may be in use. Long term, it would be ideal to remove
      // employee role and related artifacts.
      deserializedJson.permission_set_id = json.permissionSetId;
    }
    return this._super(typeClass, deserializedJson);
  },
  serialize(snapshot, options) {
    const json = this._super(snapshot, options);
    const data = {
      ...json,
      ...options.overrides
    };
    delete data.invitation_status;
    const assignmentType = data.can_access_all_units ? ALL_CURRENT_AND_FUTURE_LOCATIONS : EXPLICIT_LOCATIONS;
    const phoneNumber = formatPhoneNumber(data.phone_number, this.userInfo.countryCode);
    return {
      assignedLocations: {
        assignmentType,
        locationIds: data.subunit_tokens
      },
      emailAddress: data.merchant_supplied_email || null,
      familyName: data.last_name,
      givenName: data.first_name,
      isAuthorizedRepresentative: data.is_authorized_user,
      jobTemplateIds: data.job_template_ids,
      permissionSetId: data.employee_role_token || data.permission_set_id || null,
      phoneNumber: phoneNumber || null,
      referenceId: data.employee_number || null,
      status: data.active ? ACTIVE : INACTIVE,
      id: data.token,
      personId: data.person_id || null,
      birthDate: data.birth_date || null,
      address: data.address || null
    };
  },
  normalizeSingleResponse(store, primaryModelClass, {
    employee
  }, id, requestType) {
    return this._super(store, primaryModelClass, employee, id, requestType);
  },
  normalizeArrayResponse(store, primaryModelClass, {
    employees
  }, id, requestType) {
    return this._super(store, primaryModelClass, employees, id, requestType);
  },
  pushPayload(store, payload) {
    const [, modelName] = this.toString().split(':');
    const modelClass = store.modelFor(modelName);
    store.push(this.normalize(modelClass, payload.employee));
  }
});